/**
 * SourceReportsSection (sourceReports) — the source-report register stamped at
 * executive-summary generation. W2c editorial port: the ad-hoc flex list moves
 * onto EditorialReferenceRegister (flat `[N]` rows — the Refs 22B treatment):
 * row = [N] · bold report-type category — title · verdict-tier tag. The data
 * (type shortLabel, title, verdictTier) is identical to the pre-port list;
 * `generatedAt` exists on the stamp but was never displayed — still omitted
 * (no content change). Empty-section semantics unchanged.
 */
import React from 'react';
import { getReportType } from '../../../../config/reportTypes';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { EditorialReferenceRegister, type ReferenceRow } from '../../report/editorial';

interface Props {
  report: SiteRagReport;
  label: string;
}

export const SourceReportsSection: React.FC<Props> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const sources = report.executiveSummarySourcesUsed ?? [];
  const rows: ReferenceRow[] = sources.map((src, i) => {
    const typeLabel = getReportType(src.reportType).shortLabel;
    const title = (src.title ?? '').trim();
    return {
      id: i + 1,
      // When the stamped title is empty the type label becomes the source so
      // the row survives the register's empty-source dropping (the pre-port
      // list rendered the type label alone in that case too).
      category: title ? typeLabel : undefined,
      source: title || typeLabel,
      tag: src.verdictTier || undefined,
    };
  });
  return (
    <SectionShell label={label} hasData={sources.length > 0} report={report} sectionId="sourceReports">
      <EditorialReferenceRegister theme={theme} rows={rows} />
    </SectionShell>
  );
};
